import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  FileText, 
  Download, 
  GitBranch, 
  MessageCircle, 
  Scale, 
  HelpCircle, 
  Menu, 
  User, 
  LogOut,
  Heart,
  Home,
  Shield,
  ChevronDown,
  Briefcase,
  Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mainNavItems = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/carte', label: 'Cartographie', icon: MapPin },
];

const ressourcesItems = [
  { path: '/parcours', label: 'Parcours', icon: GitBranch },
  { path: '/assistant', label: 'Assistant IA', icon: MessageCircle },
  { path: '/documentation', label: 'Documentation', icon: FileText },
  { path: '/outils', label: 'Outils & Courriers', icon: Download },
  { path: '/avocats', label: 'Avocats', icon: Scale },
  { path: '/faq', label: 'FAQ', icon: HelpCircle },
  { path: '/temoignages', label: 'Témoignages', icon: MessageCircle },
];

export default function Layout() {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isActive = (path) => location.pathname === path;
  const isRessourceActive = ressourcesItems.some(item => location.pathname === item.path);

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [token]);

  useEffect(() => {
    if (user && token) {
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token, fetchUnreadCount]);

  const NavLinks = ({ mobile = false }) => (
    <>
      {mainNavItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => mobile && setMobileOpen(false)}
          className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors rounded-full ${
            isActive(item.path)
              ? 'bg-primary text-primary-foreground'
              : 'text-foreground hover:bg-accent'
          }`}
          data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </Link>
      ))}
      
      {/* Desktop dropdown for Organiser ma lutte */}
      {!mobile && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className={`gap-2 px-3 py-2 text-sm font-medium rounded-full ${
                isRessourceActive ? 'bg-primary text-primary-foreground' : ''
              }`}
              data-testid="nav-ressources-dropdown"
            >
              <Briefcase className="w-4 h-4" />
              Organiser ma lutte
              <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {ressourcesItems.map((item) => (
              <DropdownMenuItem key={item.path} asChild>
                <Link
                  to={item.path}
                  className={`flex items-center gap-2 w-full ${
                    isActive(item.path) ? 'text-primary font-medium' : ''
                  }`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      
      {/* Mobile: show all items */}
      {mobile && ressourcesItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors rounded-full ${
            isActive(item.path)
              ? 'bg-primary text-primary-foreground'
              : 'text-foreground hover:bg-accent'
          }`}
          data-testid={`nav-${item.path.replace('/', '')}`}
        >
          <item.icon className="w-4 h-4" />
          {item.label}
        </Link>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-9 h-9 rounded-sm bg-primary flex items-center justify-center shrink-0">
                <Home className="w-4 h-4 text-primary-foreground" />
              </div>
              <span
                className="font-serif font-bold hidden sm:flex flex-col leading-tight"
                style={{ fontSize: 'clamp(0.7rem, 0.95vw, 1rem)' }}
                data-testid="logo-text"
              >
                <span className="text-foreground whitespace-nowrap">Collectif de Défense de</span>
                <span className="text-primary whitespace-nowrap">l'Habitat Léger</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <NavLinks />
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Donate Button */}
              <a
                href="https://www.helloasso.com/associations/desobeissance-fertile/formulaires/1"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex"
                data-testid="donate-button"
              >
                <Button variant="outline" size="sm" className="gap-2">
                  <Heart className="w-4 h-4" />
                  Faire un don
                </Button>
              </a>

              {/* Auth */}
              {user ? (
                <div className="flex items-center gap-2">
                  {/* Messages link with notification badge */}
                  <Link to="/messages" data-testid="messages-link">
                    <Button variant="ghost" size="sm" className="gap-2 relative">
                      <Mail className="w-4 h-4" />
                      <span className="hidden sm:inline">Messages</span>
                      {unreadCount > 0 && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                          data-testid="unread-badge"
                        >
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                  {/* Admin link for admins */}
                  {user.is_admin && (
                    <Link to="/admin" data-testid="admin-link">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <Shield className="w-4 h-4" />
                        <span className="hidden sm:inline">Admin</span>
                      </Button>
                    </Link>
                  )}
                  <Link to="/profil" data-testid="profile-link">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <User className="w-4 h-4" />
                      <span className="hidden sm:inline">{user.name}</span>
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={logout}
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Link to="/connexion" data-testid="login-link">
                  <Button size="sm">Connexion</Button>
                </Link>
              )}

              {/* Mobile Menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <div className="flex flex-col gap-4 mt-8">
                    <NavLinks mobile />
                    <hr className="border-border" />
                    <a
                      href="https://www.helloasso.com/associations/desobeissance-fertile/formulaires/1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary"
                    >
                      <Heart className="w-4 h-4" />
                      Faire un don
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-serif font-bold text-lg mb-4">Collectif Habitat Léger</h3>
              <p className="text-sm text-muted-foreground">
                Défendre le droit de vivre autrement. Ensemble, nous sommes plus forts.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Ressources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/documentation" className="hover:text-primary">Documentation</Link></li>
                <li><Link to="/outils" className="hover:text-primary">Courriers types</Link></li>
                <li><Link to="/faq" className="hover:text-primary">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Soutenir</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Aidez-nous à continuer notre mission de défense de l'habitat léger.
              </p>
              <a
                href="https://www.helloasso.com/associations/desobeissance-fertile/formulaires/1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="gap-2">
                  <Heart className="w-4 h-4" />
                  Faire un don
                </Button>
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Collectif Habitat Léger. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
